// Ship-to addresses: where a customer takes delivery, where a supplier takes an order. The types,
// a `useShipToAddresses()` hook, the section a party card shows them in, and a picker for a
// document header that has to say where the goods go.
//
// A card has one address, and that is where the invoice goes. Goods go to a shop, a site, a depot
// — places that used to be typed into the delivery note's free text afresh each time. They are
// rows with a code here, so a document picks "RIGA-2" and the note prints the same lines every time.
import { del, type Party, post, put, qs, useAction, useApi } from "@keel/core";
import { useState } from "react";
import { Item, ItemList, Section } from "../Section";
import { Select } from "../Select";
import { Empty, Field, Icon, Modal, Switch } from "../ui";
import type { PartyKind } from "./contacts";

export interface ShipToAddress {
  id: string;
  partyKind: PartyKind;
  partyId: string;
  /** Short and upper-cased; unique per party. What a document header shows. */
  code: string;
  /** Who the parcel is addressed to: the party, a branch, a site. */
  name: string;
  address: string;
  address2: string | null;
  city: string;
  postalCode: string;
  /** ISO 3166-1 alpha-2, upper-cased. */
  country: string;
  contactName: string | null;
  phone: string | null;
  email: string | null;
  /** The one a new document takes when nothing else is chosen. At most one per party. */
  isDefault: boolean;
  /** A closed shop stays on the delivery notes that went there and is not offered for new ones. */
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/** What the API takes on POST/PUT. The card is named on create only; an address never moves. */
export interface ShipToAddressInput {
  partyKind?: PartyKind;
  partyId?: string;
  code: string;
  name: string;
  address: string;
  address2?: string | null;
  city: string;
  postalCode: string;
  country: string;
  contactName?: string | null;
  phone?: string | null;
  email?: string | null;
  isDefault?: boolean;
  isActive?: boolean;
}

/** What `GET /ship-to-addresses/{id}/formatted` answers: the label as it prints. */
export interface FormattedShipTo {
  id: string;
  lines: string[];
  text: string;
}

/**
 * Countries that write the postal code before the city ("LV-1050 Rīga"). Mirrors the server's
 * list closely enough for a preview; the delivery note itself asks the server for the label.
 */
const POSTAL_CODE_FIRST = new Set([
  "LV",
  "EE",
  "LT",
  "DE",
  "AT",
  "CH",
  "PL",
  "FI",
  "SE",
  "NO",
  "DK",
  "IS",
  "NL",
  "BE",
  "LU",
  "FR",
  "ES",
  "PT",
  "IT",
  "CZ",
  "SK",
  "HU",
  "SI",
  "HR",
  "RO",
  "BG",
  "GR",
  "UA",
]);

/** "LV-1050 Rīga" or "London SW1A 1AA", the way the country writes it. */
export const postalLine = (a: Pick<ShipToAddress, "city" | "postalCode" | "country">) =>
  POSTAL_CODE_FIRST.has(a.country.toUpperCase()) ? `${a.postalCode} ${a.city}` : `${a.city} ${a.postalCode}`;

/**
 * The delivery points of one card. Nothing is fetched until the card's id is known, so a form for
 * a new party can mount this before it has been saved.
 */
export function useShipToAddresses(
  partyKind: PartyKind,
  partyId: string | null | undefined,
  opts: { includeInactive?: boolean } = {},
) {
  return useApi<ShipToAddress[]>(
    partyId ? `/ship-to-addresses${qs({ partyKind, partyId, includeInactive: opts.includeInactive })}` : null,
  );
}

export interface ShipToSelectProps {
  partyKind: PartyKind;
  partyId: string | null | undefined;
  /** The chosen address's id; `null` means the party's main address. */
  value: string | null | undefined;
  onChange: (id: string | null, address: ShipToAddress | undefined) => void;
  /** The first option, standing for the address on the card itself. */
  mainLabel?: string;
  disabled?: boolean;
}

/**
 * Picking where a document's goods go. The card's own address is always on offer first, because
 * most parties have no other; the default delivery point comes next because that is almost always
 * the answer when they do.
 */
export function ShipToSelect({
  partyKind,
  partyId,
  value,
  onChange,
  mainLabel = "Use the main address",
  disabled,
}: ShipToSelectProps) {
  const addresses = useShipToAddresses(partyKind, partyId).data ?? [];
  // The main address is shown with its city, so "main" and "RIGA-1" can be told apart at a glance.
  const party = useApi<Party>(partyId ? `/${partyKind}s/${partyId}` : null).data;
  return (
    <Select
      value={value ?? ""}
      onChange={(e) =>
        onChange(
          e.target.value || null,
          addresses.find((a) => a.id === e.target.value),
        )
      }
      disabled={disabled || !partyId}
      placeholder={partyId ? undefined : "Choose the party first"}
    >
      <option value="">
        {mainLabel}
        {party?.city ? ` · ${party.city}` : ""}
      </option>
      {addresses.map((a) => (
        <option key={a.id} value={a.id}>
          {a.code} · {a.city}
          {a.isDefault ? " · default" : ""}
        </option>
      ))}
    </Select>
  );
}

/** The same control with its label. */
export function ShipToField({ label, hint, ...rest }: ShipToSelectProps & { label: string; hint?: string }) {
  return (
    <Field label={label} hint={hint}>
      <ShipToSelect {...rest} />
    </Field>
  );
}

export interface PartyAddressesProps {
  partyKind: PartyKind;
  partyId: string;
  /** The party's own write right (`masterdata.customers.write` / `masterdata.suppliers.write`). */
  canWrite: boolean;
  /** Stable id for the section to remember whether it is open. */
  id?: string;
  defaultOpen?: boolean;
}

type Draft = Partial<ShipToAddress>;

/** What the server refuses an address without, in the order the form shows them. */
const REQUIRED: [keyof ShipToAddressInput, string][] = [
  ["code", "Code"],
  ["name", "Name"],
  ["address", "Address"],
  ["postalCode", "Postal code"],
  ["city", "City"],
  ["country", "Country"],
];

/**
 * The "Ship-to addresses" section of a party card: lists, adds, edits and retires delivery
 * points in place.
 *
 * Retiring is one click and no dialog, because it is undone by one tick on the edit form; the
 * retired stay reachable behind "show retired" so a closed shop can be reopened or looked up.
 */
export function PartyAddresses({
  partyKind,
  partyId,
  canWrite,
  id = "party-ship-to",
  defaultOpen,
}: PartyAddressesProps) {
  // One query for both views: the active list is the default, the retired are filtered in.
  const { data } = useShipToAddresses(partyKind, partyId, { includeInactive: true });
  const [showRetired, setShowRetired] = useState(false);
  const [edit, setEdit] = useState<Draft | null>(null);
  const [missing, setMissing] = useState<string | null>(null);
  const { run, busy } = useAction();
  const all = data ?? [];
  const active = all.filter((a) => a.isActive);
  const retired = all.length - active.length;
  const shown = showRetired ? all : active;
  const noun = partyKind === "customer" ? "delivery" : "order";

  const save = async () => {
    if (!edit) return;
    // Said here, in the dialog, rather than by a refusal from the server the person never sees.
    const blank = REQUIRED.filter(([k]) => !String(edit[k] ?? "").trim()).map(([, label]) => label);
    if (blank.length > 0) {
      setMissing(`Fill in ${blank.join(", ")} to save this address.`);
      return;
    }
    setMissing(null);
    const body: ShipToAddressInput = {
      code: edit.code ?? "",
      name: edit.name ?? "",
      address: edit.address ?? "",
      address2: edit.address2 ?? null,
      city: edit.city ?? "",
      postalCode: edit.postalCode ?? "",
      country: edit.country ?? "",
      contactName: edit.contactName ?? null,
      phone: edit.phone ?? null,
      email: edit.email ?? null,
      isDefault: edit.isDefault ?? false,
      isActive: edit.isActive ?? true,
    };
    const saved = await run(
      () =>
        edit.id
          ? put(`/ship-to-addresses/${edit.id}`, body)
          : post("/ship-to-addresses", { ...body, partyKind, partyId }),
      edit.id ? "Address saved" : "Address added",
    );
    if (saved) setEdit(null);
  };

  const retire = (a: ShipToAddress) => run(() => del(`/ship-to-addresses/${a.id}`), `${a.code} retired`);

  return (
    <Section
      title="Ship-to addresses"
      id={id}
      defaultOpen={defaultOpen}
      count={active.length || undefined}
      action={
        canWrite && (
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => setEdit({ country: "LV", isDefault: active.length === 0, isActive: true })}
          >
            <Icon name="plus" size={14} />
            Add
          </button>
        )
      }
    >
      {data && shown.length === 0 ? (
        <Empty
          title="Everything goes to the main address"
          icon="truck"
          text={
            canWrite
              ? `Add the shops, sites or depots that take ${noun} so a document can pick one by code instead of re-typing it.`
              : `No ${noun} addresses have been added to this card.`
          }
        />
      ) : (
        <ItemList>
          {shown.map((a) => (
            <Item
              key={a.id}
              muted={!a.isActive}
              title={a.code}
              tags={
                <>
                  {a.isDefault && a.isActive && <span className="perm-tag">default</span>}
                  {!a.isActive && <span className="perm-tag">retired</span>}
                </>
              }
              actions={
                canWrite && (
                  <>
                    <button
                      type="button"
                      className="icon-btn"
                      onClick={() => setEdit(a)}
                      title="Edit"
                      aria-label={`Edit ${a.code}`}
                    >
                      <Icon name="edit" size={14} />
                    </button>
                    {a.isActive && (
                      <button
                        type="button"
                        className="icon-btn"
                        onClick={() => retire(a)}
                        disabled={busy}
                        title="Retire"
                        aria-label={`Retire ${a.code}`}
                      >
                        <Icon name="trash" size={14} />
                      </button>
                    )}
                  </>
                )
              }
            >
              {a.name && <div>{a.name}</div>}
              <div>
                {a.address}
                {a.address2 ? `, ${a.address2}` : ""}, {postalLine(a)}, {a.country}
              </div>
              {(a.contactName || a.phone || a.email) && (
                <div>
                  {a.contactName}
                  {a.contactName && (a.phone || a.email) ? " · " : ""}
                  {a.phone && <a href={`tel:${a.phone}`}>{a.phone}</a>}
                  {a.phone && a.email ? " · " : ""}
                  {a.email && <a href={`mailto:${a.email}`}>{a.email}</a>}
                </div>
              )}
            </Item>
          ))}
        </ItemList>
      )}
      {retired > 0 && (
        <p className="muted sm">
          <button type="button" className="link" onClick={() => setShowRetired((s) => !s)}>
            {showRetired ? "Hide retired" : `Show ${retired} retired`}
          </button>
        </p>
      )}
      {edit && (
        <Modal
          title={edit.id ? `Ship-to address · ${edit.code}` : "New ship-to address"}
          onClose={() => {
            setEdit(null);
            setMissing(null);
          }}
          busy={busy}
          error={missing}
          submit={edit.id ? "Save" : "Add"}
          onSubmit={save}
        >
          <div className="stack">
            <div className="form-grid">
              <Field required label="Code" hint="Short; what the document header shows">
                <input
                  value={edit.code ?? ""}
                  aria-required
                  onChange={(e) => setEdit({ ...edit, code: e.target.value.toUpperCase() })}
                  placeholder="SHOP-1"
                  maxLength={20}
                />
              </Field>
              <Field required label="Name" hint="Who the parcel is addressed to">
                <input
                  value={edit.name ?? ""}
                  aria-required
                  onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                  placeholder="Ship-to Co, Rīga shop"
                />
              </Field>
              <Field required label="Address" span>
                <input
                  value={edit.address ?? ""}
                  aria-required
                  onChange={(e) => setEdit({ ...edit, address: e.target.value })}
                  placeholder="Brīvības iela 1"
                />
              </Field>
              <Field label="Address, line 2" span>
                <input
                  value={edit.address2 ?? ""}
                  onChange={(e) => setEdit({ ...edit, address2: e.target.value })}
                  placeholder="Entrance B, 3rd floor"
                />
              </Field>
              <Field required label="Postal code">
                <input
                  value={edit.postalCode ?? ""}
                  aria-required
                  onChange={(e) => setEdit({ ...edit, postalCode: e.target.value })}
                  placeholder="LV-1010"
                />
              </Field>
              <Field required label="City">
                <input
                  value={edit.city ?? ""}
                  aria-required
                  onChange={(e) => setEdit({ ...edit, city: e.target.value })}
                  placeholder="Rīga"
                />
              </Field>
              <Field required label="Country" hint="Two-letter ISO code; decides how the label prints">
                <input
                  value={edit.country ?? ""}
                  aria-required
                  onChange={(e) => setEdit({ ...edit, country: e.target.value.toUpperCase() })}
                  placeholder="LV"
                  maxLength={2}
                />
              </Field>
              <Field label="Contact at this address">
                <input
                  value={edit.contactName ?? ""}
                  onChange={(e) => setEdit({ ...edit, contactName: e.target.value })}
                  placeholder="Dace Ozola"
                />
              </Field>
              <Field label="Phone">
                <input
                  value={edit.phone ?? ""}
                  onChange={(e) => setEdit({ ...edit, phone: e.target.value })}
                  placeholder="+371 2000 0000"
                />
              </Field>
              <Field label="Email">
                <input
                  type="email"
                  value={edit.email ?? ""}
                  onChange={(e) => setEdit({ ...edit, email: e.target.value })}
                  placeholder="shop@example.com"
                />
              </Field>
            </div>
            <Switch
              checked={edit.isDefault ?? false}
              onChange={(isDefault) => setEdit({ ...edit, isDefault })}
              label="The default ship-to address"
              hint="Where a new document goes when nobody picks one. Only one address holds it; ticking it here takes it from whichever had it."
            />
            {edit.id && (
              <Switch
                checked={edit.isActive !== false}
                onChange={(isActive) => setEdit({ ...edit, isActive })}
                label={edit.isActive !== false ? "On the card" : "Retired"}
                hint="A retired address stays on the delivery notes that went there and is not offered for new ones."
              />
            )}
          </div>
        </Modal>
      )}
    </Section>
  );
}
